import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import adminApi, {
  AdminUserSummary,
  ASSIGNABLE_ROLES,
  AssignableRole,
  SaasInvoice,
  SubscriberDetail as SubscriberDetailDto,
  SubscriptionPlan,
} from '../../api/admin';
import { auth as tokenStore } from '../../api/axios';
import { safeStorage } from '../../utils/safeStorage';
import { useLanguage } from '../../contexts/LanguageContext';

const SubscriberDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const subId = Number(id);
  const { t } = useLanguage();
  const [detail, setDetail] = useState<SubscriberDetailDto | null>(null);
  const [invoices, setInvoices] = useState<SaasInvoice[]>([]);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [tempPassword, setTempPassword] = useState<{ user: AdminUserSummary; password: string } | null>(null);
  const [period, setPeriod] = useState('');
  const [periodUsage, setPeriodUsage] = useState<{ periodLabel: string; totals: Record<string, number> } | null>(null);

  const loadUsage = async (p: string) => {
    setPeriod(p);
    if (!p) { setPeriodUsage(null); return; }
    try {
      setPeriodUsage(await adminApi.getSubscriberUsage(subId, p));
    } catch {
      setPeriodUsage(null);
    }
  };

  const refresh = () => {
    setLoading(true);
    Promise.all([
      adminApi.getSubscriber(subId),
      adminApi.listInvoices(undefined, 0, 50).then((p) => p.content.filter((i) => i.companyId === subId)),
      adminApi.listSubscriberUsers(subId).catch(() => [] as AdminUserSummary[]),
    ])
      .then(([d, inv, u]) => {
        setDetail(d);
        setInvoices(inv);
        setUsers(u);
      })
      .catch((e) => toast.error(e?.message ?? 'Failed'))
      .finally(() => setLoading(false));
  };

  const handleRoleChange = async (u: AdminUserSummary, role: AssignableRole) => {
    if (role === u.role) return;
    setUpdating(true);
    try {
      const updated = await adminApi.updateUserRole(u.id, role);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      toast.success(`Rôle de ${u.email} mis à jour : ${role}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors du changement de rôle');
    } finally {
      setUpdating(false);
    }
  };

  const handleResetPassword = async (u: AdminUserSummary) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.email} ?`)) return;
    setUpdating(true);
    try {
      const r = await adminApi.resetUserPassword(u.id);
      setTempPassword({ user: u, password: r.temporaryPassword });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setUpdating(false); }
  };

  const handleImpersonate = async (u: AdminUserSummary) => {
    if (!window.confirm(
      `Se connecter en tant que ${u.email} ?\n\n` +
      `Tu seras déconnecté de ton compte admin actuel. Pour revenir au backoffice, tu devras te déconnecter et te reconnecter avec ton compte admin.`,
    )) return;
    setUpdating(true);
    try {
      const r = await adminApi.impersonateUser(u.id);
      // Replace the admin's token with the impersonation token. We don't have a
      // refresh token here on purpose — when this access token expires (~minutes),
      // the user is forced back to /login as their normal self.
      tokenStore.clear();
      safeStorage.setItem('tf_access_token', r.accessToken);
      tokenStore.setCompanyId(r.companyId);
      // Hard reload to /  so AuthProvider re-hydrates from the new token and
      // RoleGate routes us into the tenant app.
      window.location.assign('/');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Impersonation échouée');
      setUpdating(false);
    }
  };

  useEffect(() => {
    if (!isNaN(subId)) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId]);

  const handleSuspend = async () => {
    if (!detail) return;
    if (!window.confirm(`Suspendre « ${detail.raisonSociale} » ? Les utilisateurs ne pourront plus se connecter.`)) return;
    setUpdating(true);
    try {
      await adminApi.suspendSubscriber(subId);
      toast.success('Compte suspendu');
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setUpdating(false); }
  };

  const handleReactivate = async () => {
    if (!detail) return;
    setUpdating(true);
    try {
      await adminApi.reactivateSubscriber(subId);
      toast.success('Compte réactivé');
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setUpdating(false); }
  };

  const handleSoftDelete = async () => {
    if (!detail) return;
    const expected = detail.raisonSociale.trim();
    const typed = window.prompt(
      `Suppression définitive (soft-delete) de « ${expected} ».\n\n` +
      `Le subscriber sera masqué de la liste mais conservé en base. Pour confirmer, ` +
      `tapez exactement la raison sociale ci-dessous :`,
      ''
    );
    if (typed === null) return;
    if (typed.trim() !== expected) { toast.error('Confirmation invalide — suppression annulée'); return; }
    setUpdating(true);
    try {
      await adminApi.softDeleteSubscriber(subId);
      toast.success('Subscriber supprimé');
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setUpdating(false); }
  };

  const handlePlanChange = async (plan: SubscriptionPlan) => {
    if (!detail || plan === detail.plan) return;
    const reason = window.prompt(
      t.admin?.subscriberDetail?.reasonPrompt ?? 'Raison du changement de plan ?',
      ''
    );
    if (reason === null) return;
    setUpdating(true);
    try {
      await adminApi.updateSubscriberPlan(subId, plan, reason);
      toast.success(t.admin?.subscriberDetail?.planUpdated ?? 'Plan mis à jour');
      refresh();
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <div className="text-slate-500">{t.common.loading}</div>;
  if (!detail) return null;

  const usageLabels: Record<string, string> = {
    INVOICE_CREATED: t.admin?.usage?.invoices ?? 'Factures créées',
    OCR_PAGE: t.admin?.usage?.ocr ?? 'Pages OCR',
    API_CALL: t.admin?.usage?.api ?? 'Appels API',
    USER_SEAT: t.admin?.usage?.seats ?? 'Sièges utilisateurs',
    DOCUMENT_CREATED: t.admin?.usage?.documents ?? 'Documents (autres)',
  };

  return (
    <div className="space-y-6">
      <Link
        to="/admin/subscribers"
        className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {t.admin?.subscriberDetail?.back ?? 'Retour à la liste'}
      </Link>

      {detail.deletedAt && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Ce subscriber a été supprimé (soft-delete) le{' '}
          {new Date(detail.deletedAt).toLocaleString()}. Cliquez sur « Réactiver »
          pour le restaurer.
        </div>
      )}
      {detail.actif === false && !detail.deletedAt && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Ce subscriber est <strong>suspendu</strong>. Les utilisateurs ne peuvent
          plus se connecter. Cliquez sur « Réactiver » pour rétablir l'accès.
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{detail.raisonSociale}</h1>
            <div className="mt-1 text-sm text-slate-500 font-mono">{detail.matriculeFiscal}</div>
            <div className="mt-3 text-sm text-slate-700">
              {detail.adresse} · {detail.country}
            </div>
            {detail.email && <div className="text-sm text-slate-600 mt-1">{detail.email}</div>}
            {detail.telephone && <div className="text-sm text-slate-600">{detail.telephone}</div>}
            <div className="mt-3 text-xs text-slate-500">
              {t.admin?.subscriberDetail?.createdAt ?? 'Inscrit le'}{' '}
              {new Date(detail.createdAt).toLocaleString()}
            </div>
          </div>

          <div className="flex flex-col gap-2 min-w-[200px]">
            <label className="text-xs uppercase tracking-wide text-slate-500 font-medium">
              {t.admin?.subscriberDetail?.plan ?? 'Plan actuel'}
            </label>
            <select
              value={detail.plan}
              disabled={updating}
              onChange={(e) => handlePlanChange(e.target.value as SubscriptionPlan)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500"
            >
              <option value="STARTER">STARTER</option>
              <option value="PRO">PRO</option>
              <option value="BUSINESS">BUSINESS</option>
            </select>
            <div className="text-xs text-slate-500">
              {detail.usersCount} {t.admin?.subscriberDetail?.users ?? 'utilisateur(s)'}
            </div>

            <div className="mt-3 pt-3 border-t border-slate-200 flex flex-col gap-2">
              {detail.actif !== false && !detail.deletedAt && (
                <button
                  type="button"
                  onClick={handleSuspend}
                  disabled={updating}
                  className="px-3 py-2 text-sm rounded-lg border border-amber-300 text-amber-700 bg-white hover:bg-amber-50 disabled:opacity-50"
                >
                  Suspendre
                </button>
              )}
              {(detail.actif === false || detail.deletedAt) && (
                <button
                  type="button"
                  onClick={handleReactivate}
                  disabled={updating}
                  className="px-3 py-2 text-sm rounded-lg border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50 disabled:opacity-50"
                >
                  Réactiver
                </button>
              )}
              {!detail.deletedAt && (
                <button
                  type="button"
                  onClick={handleSoftDelete}
                  disabled={updating}
                  className="px-3 py-2 text-sm rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                >
                  Supprimer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Utilisateurs ({users.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Nom</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Email</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Rôle</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Statut</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    Aucun utilisateur
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-800">{u.prenom} {u.nom}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.role === 'PLATFORM_ADMIN' ? (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">PLATFORM_ADMIN</span>
                    ) : (
                      <select
                        value={u.role}
                        disabled={updating}
                        onChange={(e) => handleRoleChange(u, e.target.value as AssignableRole)}
                        className="px-2 py-1 border border-slate-300 rounded text-xs bg-white focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {ASSIGNABLE_ROLES.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                        {!ASSIGNABLE_ROLES.includes(u.role as AssignableRole) && (
                          <option value={u.role}>{u.role}</option>
                        )}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.actif ? (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-800">Actif</span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-slate-200 text-slate-700">Désactivé</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleResetPassword(u)}
                      disabled={updating}
                      className="px-2 py-1 text-xs rounded border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      Réinitialiser mot de passe
                    </button>
                    <button
                      type="button"
                      onClick={() => handleImpersonate(u)}
                      disabled={updating || !u.actif}
                      className="px-2 py-1 text-xs rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                    >
                      Se connecter en tant que
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {tempPassword && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">Mot de passe temporaire</h3>
            <p className="text-sm text-slate-600">
              Mot de passe généré pour <strong>{tempPassword.user.email}</strong>. Copie-le et
              transmets-le par un canal sûr. Il ne sera plus visible après fermeture.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 font-mono text-center text-lg break-all">
              {tempPassword.password}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(tempPassword.password);
                  toast.success('Copié dans le presse-papier');
                }}
                className="px-3 py-2 text-sm rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                Copier
              </button>
              <button
                type="button"
                onClick={() => setTempPassword(null)}
                className="px-3 py-2 text-sm rounded-lg bg-slate-800 text-white hover:bg-slate-900"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {t.admin?.subscriberDetail?.usageTitle ?? 'Utilisation'}
            {' '}({(periodUsage ?? detail.usageThisMonth).periodLabel})
          </h2>
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={period}
              onChange={(e) => loadUsage(e.target.value)}
              className="text-sm rounded-lg border-slate-300"
            />
            {period && (
              <button onClick={() => loadUsage('')} className="text-xs text-slate-500 hover:underline">
                Mois courant
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries((periodUsage ?? detail.usageThisMonth).totals).map(([key, val]) => (
            <div key={key} className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-slate-900">{val}</div>
              <div className="text-xs text-slate-500 mt-1">{usageLabels[key] ?? key}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">
            {t.admin?.subscriberDetail?.invoicesTitle ?? 'Factures SaaS'}
          </h2>
        </div>
        {invoices.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">
            {t.admin?.subscriberDetail?.noInvoices ?? 'Aucune facture SaaS pour cet abonné.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-600">
                  {t.admin?.subscriberDetail?.period ?? 'Période'}
                </th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Plan</th>
                <th className="px-4 py-2 text-right font-medium text-slate-600">
                  {t.admin?.subscriberDetail?.amount ?? 'Montant TTC'}
                </th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td className="px-4 py-2 text-slate-700">
                    {inv.periodStart} → {inv.periodEnd}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{inv.plan}</td>
                  <td className="px-4 py-2 text-right font-medium text-slate-900">
                    {inv.amountTtc.toFixed(3)} TND
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={inv.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: SaasInvoice['status'] }> = ({ status }) => {
  const tone =
    status === 'PAID'
      ? 'bg-green-100 text-green-700'
      : status === 'PENDING'
      ? 'bg-amber-100 text-amber-700'
      : status === 'FAILED'
      ? 'bg-red-100 text-red-700'
      : 'bg-slate-100 text-slate-700';
  return <span className={`px-2 py-1 rounded text-xs font-semibold ${tone}`}>{status}</span>;
};

export default SubscriberDetail;
